import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAuth } from '../../hooks/useAuth';
import SideMenuLayout from '../layout/SideMenuLayout';
import Layout from '../layout/Layout';

const withMailboxHref = (children, href) =>
    React.cloneElement(children, { mailboxHref: href });

/**
 * Link view route - wraps message/document/documenso view and reply routes.
 * - Authenticated users: SideMenuLayout
 * - Free account users (logged in): Layout
 * - Unauthenticated with verified session (rid+access code, no login):
 *   - Sender preview routes (/preview/): redirect to /login
 *   - Reply routes (/reply/): allow
 *   - View routes (/link/:id, /document/:id, /documenso/link/:id): allow - they verified via access code
 * - Unauthenticated with no session: redirect to /mailbox?rid=:id for verification flow
 */
const LinkViewRoute = ({ children }) => {
    const { isLoggedIn } = useAuth();
    const { unique_identifier } = useParams();
    const location = useLocation();
    const pathname = location.pathname || '';
    const [mailboxStatus, setMailboxStatus] = useState(null);

    useEffect(() => {
        if (isLoggedIn) {
            setMailboxStatus({ isFreeAccount: false, hasMailboxAccess: true });
            return;
        }
        let cancelled = false;
        fetch('/api/contacts-mailbox/status/', { credentials: 'include' })
            .then((res) => res.json())
            .then((data) => {
                if (!cancelled) {
                    setMailboxStatus({
                        isFreeAccount: data.is_free_account === true,
                        hasMailboxAccess: data.has_mailbox_access === true,
                    });
                }
            })
            .catch(() => {
                if (!cancelled) setMailboxStatus({ isFreeAccount: false, hasMailboxAccess: false });
            });
        return () => { cancelled = true; };
    }, [isLoggedIn]);

    if (isLoggedIn) {
        return <SideMenuLayout>{withMailboxHref(children, '/profile/mailbox')}</SideMenuLayout>;
    }

    if (mailboxStatus === null) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (mailboxStatus.isFreeAccount) {
        return <Layout>{withMailboxHref(children, '/mailbox')}</Layout>;
    }

    const isPreviewRoute = pathname.includes('/preview/');
    if (isPreviewRoute) {
        return <Navigate to={{ pathname: '/login', search: `?next=${encodeURIComponent(pathname)}` }} replace />;
    }

    const isReplyRoute = pathname.includes('/reply/');
    if (isReplyRoute) {
        return <Layout>{withMailboxHref(children, null)}</Layout>;
    }

    if (mailboxStatus.hasMailboxAccess) {
        return <Layout>{withMailboxHref(children, null)}</Layout>;
    }

    return <Navigate to={`/mailbox?rid=${unique_identifier || ''}`} replace />;
};

export default LinkViewRoute;
